import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai/index';
import { useState } from 'react';

import { useOverlayItem } from '@flipster/overlay';
import { Button, Toggle } from '@flipster/react/design-system';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { useResponsive } from '@flipster/react/misc/responsive/legacy';
import { css } from '@flipster/styled-system/css';
import { hstack } from '@flipster/styled-system/patterns';
import { Drawer } from '@flipster/ui';
import segment from '@flipster/utils/segment';
import { TradingLeaderboardEvent } from '@flipster/utils/segment/events/trading-leaderboard';

import { intervalLeaderboardMyRankingBunja } from '../../bunja';

interface Props {
  initialSettings: { optOut: boolean };
  onFinish: () => void;
}

export const SettingsOverlay = ({ initialSettings, onFinish }: Props) => {
  const { onClose } = useOverlayItem();
  // Drawer 컴포넌트가 useResponsive 기반이라서 부득이하게 media query 대신 적용하였음
  const { isMobile } = useResponsive();
  const [isOptOut, setIsOptOut] = useState(initialSettings.optOut);

  const { putTradingLeaderboardsOptOutAtom } = useBunja(intervalLeaderboardMyRankingBunja);
  const optOutMutation = useAtomValue(putTradingLeaderboardsOptOutAtom);

  const handleToggleOptOut = () => {
    setIsOptOut((v) => !v);
  };

  const handleClickDoneSettings = async () => {
    if (isOptOut !== initialSettings.optOut) {
      segment.track(TradingLeaderboardEvent.LeaderboardSettingConfirmClick, { view_my_rank: !isOptOut }); // opt-in: true, opt-out: false
      await optOutMutation.mutateAsync({ optOut: isOptOut });
    }
    onFinish();
  };

  if (!isMobile) {
    return (
      <Modal>
        <Modal.Close onClose={onClose} />
        <Modal.Header>
          <Modal.Title label='Settings' />
        </Modal.Header>
        <Modal.Content>
          <div className={css({ bg: 'background.default.level-2', rounded: 8, p: '12px 16px' })}>
            <div className={hstack({ justify: 'space-between', mb: 8 })}>
              <span className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-4' })}>
                View my Rank
              </span>
              <Toggle selected={!isOptOut} onChange={handleToggleOptOut} />
            </div>
            <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
              Your ranking is shown on the leaderboard by default. If disabled, it won't appear in the rankings.
            </p>
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Button label='Done' onClick={handleClickDoneSettings} size='large' />
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Drawer isShow position='bottom' className={css({ bg: 'linear-gradient(179deg, #1C1C1C 46.21%, #111 87.14%)' })}>
      <div className={css({ pb: 20, textStyle: 'Title/S', color: 'content.default.level-5' })}>Settings</div>
      <div className={css({ mb: 32, bg: 'background.default.level-2', rounded: 8, p: '12px 16px' })}>
        <div className={hstack({ justify: 'space-between', mb: 8 })}>
          <span className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-4' })}>View my Rank</span>
          <Toggle selected={!isOptOut} onChange={handleToggleOptOut} />
        </div>
        <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>
          Your ranking is shown on the leaderboard by default. If disabled, it won't appear in the rankings.
        </p>
      </div>
      <Button label='Done' onClick={handleClickDoneSettings} size='large' css={css.raw({ width: '100%' })} />
    </Drawer>
  );
};
